import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  MAX_PHOTO_BYTES,
  PHOTO_CONTENT_TYPES,
  submitFeedbackBodySchema,
  type Consent,
  type PhotoContentType,
  type PublicFeedbackRequest,
  type TernaryAnswer,
} from '@fois-utsavvoice/shared';
import { ApiError } from '../api/client.js';
import {
  getPublicFeedback,
  requestPhotoUploadUrl,
  submitPublicFeedback,
  uploadPhotoToPresignedUrl,
} from '../api/public.js';
import { ChoiceGroup } from '../components/ChoiceGroup.js';
import { PageShell } from '../components/PageShell.js';
import { StarRating } from '../components/StarRating.js';

const RECOMMEND_CHOICES = [
  { value: 'YES', label: 'Yes' },
  { value: 'MAYBE', label: 'Maybe' },
  { value: 'NO', label: 'No' },
] as const;

const CONSENT_CHOICES = [
  { value: 'NAMED', label: 'Yes, with my display name' },
  { value: 'ANONYMOUS', label: 'Yes, anonymously' },
  { value: 'PRIVATE', label: 'No, keep my feedback private' },
] as const;

const UNAVAILABLE_COPY =
  'This feedback link is no longer available. Please contact FESTIVALSOFINDIASTORE if you would still like to share your experience.';

type FormErrors = Partial<Record<string, string>>;

export function FeedbackFormPage() {
  const { token } = useParams<{ token: string }>();
  const idempotencyKey = useRef(crypto.randomUUID());
  const [request, setRequest] = useState<PublicFeedbackRequest | undefined>();
  const [pageState, setPageState] = useState<
    'loading' | 'form' | 'success' | 'submitted' | 'unavailable'
  >('loading');
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState<string | undefined>();
  const [overallRating, setOverallRating] = useState<number>();
  const [productRating, setProductRating] = useState<number>();
  const [packagingRating, setPackagingRating] = useState<number>();
  const [valueRating, setValueRating] = useState<number>();
  const [deliveryRating, setDeliveryRating] = useState<number | 'na'>();
  const [likedMost, setLikedMost] = useState('');
  const [improvement, setImprovement] = useState('');
  const [recommendation, setRecommendation] = useState<TernaryAnswer>();
  const [purchaseAgain, setPurchaseAgain] = useState<TernaryAnswer>();
  const [displayName, setDisplayName] = useState('');
  const [consent, setConsent] = useState<Consent>();
  const [photoFile, setPhotoFile] = useState<File | undefined>();
  const [photoError, setPhotoError] = useState<string | undefined>();

  useEffect(() => {
    if (!token) {
      setPageState('unavailable');
      return;
    }
    getPublicFeedback(token)
      .then((data) => {
        setRequest(data);
        setPageState(data.status === 'SUBMITTED' ? 'submitted' : 'form');
      })
      .catch((error: unknown) => {
        if (error instanceof ApiError && (error.status === 410 || error.status === 404)) {
          setPageState('unavailable');
          return;
        }
        setPageState('unavailable');
      });
  }, [token]);

  const payload = useMemo(
    () => ({
      overallRating,
      productRating,
      packagingRating,
      valueRating,
      deliveryRating:
        deliveryRating === 'na' || deliveryRating === undefined ? null : deliveryRating,
      likedMost: likedMost.trim() || undefined,
      improvement: improvement.trim() || undefined,
      recommendation,
      purchaseAgain,
      displayName: displayName.trim() || undefined,
      consent,
    }),
    [
      overallRating,
      productRating,
      packagingRating,
      valueRating,
      deliveryRating,
      likedMost,
      improvement,
      recommendation,
      purchaseAgain,
      displayName,
      consent,
    ],
  );

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!token) {
      return;
    }
    const parsed = submitFeedbackBodySchema.safeParse(payload);
    if (!parsed.success) {
      const next: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        next[key] = 'This field is required.';
      }
      setErrors(next);
      return;
    }
    setErrors({});
    setFormError(undefined);
    setPhotoError(undefined);
    setSubmitting(true);
    try {
      let photoObjectId: string | null = null;
      if (photoFile) {
        const contentType = photoFile.type as PhotoContentType;
        const fileExtension = extensionForFile(photoFile);
        if (
          !PHOTO_CONTENT_TYPES.includes(contentType) ||
          !fileExtension ||
          photoFile.size > MAX_PHOTO_BYTES
        ) {
          setPhotoError('Use one JPEG, PNG, or WebP image of 5 MB or less.');
          setSubmitting(false);
          return;
        }
        const grant = await requestPhotoUploadUrl(token, {
          contentType,
          fileSize: photoFile.size,
          fileExtension,
        });
        await uploadPhotoToPresignedUrl(grant.uploadUrl, photoFile, grant.headers);
        photoObjectId = grant.photoObjectId;
      }
      await submitPublicFeedback(token, { ...parsed.data, photoObjectId }, idempotencyKey.current);
      setPageState('success');
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setPageState('submitted');
      } else if (error instanceof ApiError && error.status === 410) {
        setPageState('unavailable');
      } else {
        setFormError('We could not save your feedback. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (pageState === 'loading') {
    return (
      <PageShell>
        <p role="status">Loading your feedback form…</p>
      </PageShell>
    );
  }

  if (pageState === 'unavailable') {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <h1 className="heading-xl text-crimson">This feedback link is no longer available</h1>
          <p className="mt-4 text-charcoal">{UNAVAILABLE_COPY}</p>
        </section>
      </PageShell>
    );
  }

  if (pageState === 'submitted') {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <h1 className="heading-xl text-crimson">Thank you</h1>
          <p className="mt-4 text-lg text-charcoal">
            Thank you—we have already received feedback for this request.
          </p>
        </section>
      </PageShell>
    );
  }

  if (pageState === 'success') {
    return (
      <PageShell>
        <section className="card mx-auto max-w-xl text-center">
          <h1 className="heading-xl text-moss">Thank you</h1>
          <p className="mt-4 text-lg text-charcoal">
            Thank you for sharing your experience. Your feedback will help us make future FOIS
            celebrations even better.
          </p>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <section className="card mx-auto w-full max-w-2xl">
        <h1 className="heading-xl text-crimson">How was your FOIS experience?</h1>
        <p className="mt-3 text-charcoal">
          Thank you for welcoming FESTIVALSOFINDIASTORE into your celebration. Your honest
          feedback helps us improve our products, packaging, and service.
        </p>
        <p className="mt-3 font-semibold text-charcoal">{request?.productName}</p>
        <p className="mt-2 text-sm text-charcoal/80">
          Honest feedback—positive or negative—is welcome. You do not need an account.
        </p>

        <form className="mt-8 space-y-6" onSubmit={onSubmit} noValidate>
          <StarRating
            name="overallRating"
            legend="Overall experience"
            value={overallRating}
            required
            error={errors.overallRating}
            onChange={setOverallRating}
          />
          <StarRating
            name="productRating"
            legend="Product selection"
            value={productRating}
            required
            error={errors.productRating}
            onChange={setProductRating}
          />
          <StarRating
            name="packagingRating"
            legend="Packaging and presentation"
            value={packagingRating}
            required
            error={errors.packagingRating}
            onChange={setPackagingRating}
          />
          <StarRating
            name="valueRating"
            legend="Value for money"
            value={valueRating}
            required
            error={errors.valueRating}
            onChange={setValueRating}
          />
          <fieldset className="space-y-2">
            <legend className="field-label mb-2">Delivery or pickup experience</legend>
            <div className="flex flex-wrap gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <label
                  key={star}
                  className={`inline-flex min-h-11 min-w-11 cursor-pointer items-center justify-center rounded-none border text-2xl ${
                    deliveryRating === star
                      ? 'border-crimson bg-crimson text-white'
                      : 'border-linen bg-white text-charcoal'
                  }`}
                >
                  <input
                    className="sr-only"
                    type="radio"
                    name="deliveryRating"
                    value={star}
                    checked={deliveryRating === star}
                    onChange={() => setDeliveryRating(star)}
                  />
                  <span aria-hidden="true">★</span>
                  <span className="sr-only">{star} stars</span>
                </label>
              ))}
              <label
                className={`choice-chip ${deliveryRating === 'na' ? 'choice-chip-selected' : ''}`}
              >
                <input
                  className="sr-only"
                  type="radio"
                  name="deliveryRating"
                  value="na"
                  checked={deliveryRating === 'na'}
                  onChange={() => setDeliveryRating('na')}
                />
                Not applicable
              </label>
            </div>
          </fieldset>

          <div>
            <label className="field-label" htmlFor="likedMost">
              What did you like most?
            </label>
            <textarea
              id="likedMost"
              name="likedMost"
              maxLength={1000}
              rows={4}
              className="field-input"
              value={likedMost}
              onChange={(event) => setLikedMost(event.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="improvement">
              What is one thing we could improve?
            </label>
            <textarea
              id="improvement"
              name="improvement"
              maxLength={1000}
              rows={4}
              className="field-input"
              value={improvement}
              onChange={(event) => setImprovement(event.target.value)}
            />
          </div>

          <ChoiceGroup
            name="recommendation"
            legend="Would you recommend FOIS?"
            value={recommendation}
            choices={RECOMMEND_CHOICES}
            required
            error={errors.recommendation}
            onChange={setRecommendation}
          />
          <ChoiceGroup
            name="purchaseAgain"
            legend="Would you purchase from FOIS again?"
            value={purchaseAgain}
            choices={RECOMMEND_CHOICES}
            required
            error={errors.purchaseAgain}
            onChange={setPurchaseAgain}
          />

          <div>
            <label className="field-label" htmlFor="displayName">
              Display name
            </label>
            <input
              id="displayName"
              name="displayName"
              maxLength={80}
              className="field-input"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>

          <ChoiceGroup
            name="consent"
            legend="Testimonial permission"
            value={consent}
            choices={CONSENT_CHOICES}
            required
            error={errors.consent}
            onChange={setConsent}
          />

          {request?.photoUploadEnabled ? (
            <div>
              <label className="field-label" htmlFor="photo">
                Optional photo
              </label>
              <input
                id="photo"
                name="photo"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="field-input"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  setPhotoFile(file);
                  setPhotoError(undefined);
                }}
              />
              <p className="mt-1 text-sm text-charcoal/70">
                One JPEG, PNG, or WebP image, 5 MB or less.
              </p>
              {photoError ? (
                <p className="mt-1 text-sm font-semibold text-crimson" role="alert">
                  {photoError}
                </p>
              ) : null}
            </div>
          ) : null}

          <p className="text-sm text-charcoal/80">
            Read the{' '}
            <Link className="font-semibold text-crimson underline" to="/privacy">
              privacy notice
            </Link>{' '}
            before you share comments or a photo.
          </p>

          {formError ? (
            <p className="text-sm font-semibold text-crimson" role="alert">
              {formError}
            </p>
          ) : null}

          <button className="btn-primary w-full" type="submit" disabled={submitting}>
            {submitting ? 'Sending…' : 'Share my feedback'}
          </button>
        </form>
      </section>
    </PageShell>
  );
}

function extensionForFile(file: File): 'jpg' | 'jpeg' | 'png' | 'webp' | undefined {
  if (file.type === 'image/jpeg') {
    return file.name.toLowerCase().endsWith('.jpeg') ? 'jpeg' : 'jpg';
  }
  if (file.type === 'image/png') {
    return 'png';
  }
  if (file.type === 'image/webp') {
    return 'webp';
  }
  return undefined;
}
